/**
 * useDisplayCurrency.js
 *
 * Tiny React context that lets any dashboard / chart re-express its
 * numbers in the user's preferred display currency (default PLN).
 *
 * IMPORTANT: this is a *presentation* filter only. Costs and revenues
 * are always **calculated** in PLN (or in each project's lead
 * ``budget_currency``) on the backend. The display switcher merely
 * asks the API to divide the canonical PLN values by today's NBP
 * rate before returning them, so the user sees them in EUR / GBP /
 * USD without any storage or aggregation logic shifting.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const LS_KEY = 'expertfix_display_currency';
const SUPPORTED = ['PLN', 'EUR', 'GBP', 'USD'];

const DisplayCurrencyCtx = createContext({
  currency: 'PLN',
  setCurrency: () => {},
  supported: SUPPORTED,
});

export function DisplayCurrencyProvider({ children, defaultCurrency = 'PLN' }) {
  const [currency, setCurrencyState] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      return SUPPORTED.includes(stored) ? stored : defaultCurrency;
    } catch {
      return defaultCurrency;
    }
  });

  const setCurrency = useCallback((next) => {
    const safe = SUPPORTED.includes(next) ? next : 'PLN';
    setCurrencyState(safe);
    try { localStorage.setItem(LS_KEY, safe); } catch { /* ignore */ }
    // Also keep it on the user's account so it follows them to other devices.
    axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/me/ui-state/display-currency`, { value: { currency: safe } })
      .catch(() => {});
  }, []);

  const { user } = useAuth();
  useEffect(() => {
    if (!user?.id) {
      setCurrencyState(defaultCurrency);
      return;
    }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/me/ui-state/display-currency`)
      .then((r) => {
        const saved = r.data?.value?.currency;
        // Nothing saved for this person: use the default, not whatever the last user picked on this browser.
        const next = SUPPORTED.includes(saved) ? saved : defaultCurrency;
        setCurrencyState(next);
        try { localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
      })
      .catch(() => {});
  }, [user?.id, defaultCurrency]);

  useEffect(() => {
    // No-op: state is already initialized from localStorage above. The
    // effect exists so future consumers can subscribe to storage events
    // for cross-tab sync if we ever need it.
  }, [currency]);

  return (
    <DisplayCurrencyCtx.Provider value={{ currency, setCurrency, supported: SUPPORTED }}>
      {children}
    </DisplayCurrencyCtx.Provider>
  );
}

export function useDisplayCurrency() {
  return useContext(DisplayCurrencyCtx);
}

export const SYMBOL = {
  PLN: 'zł', EUR: '€', GBP: '£', USD: '$',
};

export function formatDisplay(value, currency) {
  if (value == null || Number.isNaN(value)) return '—';
  const cur = currency || 'PLN';
  const formatted = Number(value).toLocaleString('pl-PL', { maximumFractionDigits: 0 });
  if (cur === 'PLN') return `${formatted} zł`;
  return `${SYMBOL[cur] || ''}${formatted}`;
}
